import { getNumberTag, getT, getUnitSystem } from '@/i18n';
import {
  ApiError,
  type AssistantPlace,
  type AssistantFollowUpField,
  type AssistantPlanContext,
  type PlaceHit,
  type RouteInterpretResult,
} from '@/lib/api';
import { ROUTE_ELEVATION_ENABLED } from '@/lib/routePlanning';
import type { ActivityType } from '@/types';
import type { PlannerPoint, PlannerSettings } from '@/lib/plannerSettings';

/**
 * A „Grout” asszisztens válaszának alkalmazása a tervezőre — tiszta függvény,
 * hogy a felület csak megjelenítsen.
 *
 * ⚠️ AZ ASSZISZTENS CSAK ELŐTÖLT. Ami a mondatból nem derült ki (`null`), az a
 * felhasználó mostani beállítása marad; tervezés nem indul magától. Spec:
 * `docs/02-funkcionalis-spec.md` → „Grout” asszisztens.
 */

export type PlaceRole = 'from' | 'to' | number;

/** A kérés szövegének plafonja — a szerver `INTERPRET_TEXT_MAX`-ja. */
export const ASSISTANT_TEXT_MAX = 400;

/** Ennyi kérés alatt kiírjuk, hány maradt mára — fölötte csak zaj lenne. */
export const ASSISTANT_QUOTA_WARN_BELOW = 10;

/** Egy hely, amit a felhasználónak kell kiválasztania a jelöltek közül. */
export interface PendingPlace {
  role: PlaceRole;
  name: string;
  candidates: PlaceHit[];
}

export interface AssistantApplication {
  /** Új mozgásforma, ha a mondat mást kért, mint ami most be van állítva. */
  activityType: ActivityType | null;
  settings: PlannerSettings;
  /** `'current'`: a jelenlegi helyzet legyen a rajt; `null`: a rajt marad. */
  from: PlannerPoint | 'current' | null;
  /** `null`: a cél marad (vagy választásra vár, lásd `pending`). */
  to: PlannerPoint | null;
  /**
   * A mondat megnevezte, de nem tudtuk beállítani (választásra vár, vagy nincs
   * találat). ⚠️ A RÉGI ÉRTÉK NEM MARADHAT: különben a „Tervezés” a korábbi
   * célhoz tervezne, miközben a felhasználó egy újat kért.
   */
  clear: ('from' | 'to')[];
  /** `null`: a megállók maradnak; különben az új lista (`null` elem: választásra vár). */
  stops: (PlannerPoint | null)[] | null;
  pending: PendingPlace[];
  /** Amit beállítottunk — a csipek szövege. */
  applied: string[];
  /** Amit kért, de nem tudunk figyelembe venni, vagy nem találtunk. */
  notes: string[];
}

const ACTIVITY: Record<'run' | 'walk' | 'bike', ActivityType> = { run: 'run', walk: 'walk', bike: 'ride' };
const ACTIVITY_LABEL: Record<ActivityType, string> = { get run() { return getT().routeAssistant.run; }, get walk() { return getT().routeAssistant.walk; }, get ride() { return getT().routeAssistant.ride; } };
const MODE_LABEL = { get loop() { return getT().routeAssistant.loop; }, get direct() { return getT().routeAssistant.direct; } } as const;
const DETOUR_LABEL = { get small() { return getT().routeAssistant.small; }, get medium() { return getT().routeAssistant.medium; }, get large() { return getT().routeAssistant.large; } } as const;
const PREFERENCE_LABEL = { get fast() { return getT().routeAssistant.fast; }, get protected() { return getT().routeAssistant.protected; }, get quiet() { return getT().routeAssistant.quiet; } } as const;
const TERRAIN_LABEL = { get flat() { return getT().routeAssistant.flat; }, get balanced() { return getT().routeAssistant.balanced; }, get hilly() { return getT().routeAssistant.hilly; } } as const;

function formatKm(km: number): string {
  if (getUnitSystem() === 'imperial') return `${new Intl.NumberFormat(getNumberTag(), { maximumFractionDigits: 2 }).format(km / 1.609344)} mi`;
  return `${getNumberTag() === 'hu-HU' ? String(km).replace('.', ',') : String(km)} km`;
}

/**
 * Egy hely feloldása: a MÁR JÓVÁHAGYOTT (memóriából jött) hely → kész pont;
 * minden más találat → a felhasználó hagyja jóvá (egy jelöltnél is); nulla →
 * megjegyzés.
 *
 * ⚠️ EGY TALÁLATNÁL IS RÁKÉRDEZÜNK (Geri, #79): „a kelenföldi plazmapontba” a
 * címkereső rossz címet adott, és kérdés nélkül beállt. A jelöltek távolság
 * szerint jönnek, de a „Petőfi utca” több is lehet a közelben.
 */
function resolvePlace(
  role: PlaceRole,
  place: AssistantPlace,
  pending: PendingPlace[],
  notes: string[],
): PlannerPoint | null {
  const name = place.name ?? '';
  const [only, ...rest] = place.candidates;
  if (!only) {
    notes.push(getT().routeAssistant.notFound(name));
    return null;
  }
  /* #86: a point the server chose for a vague destination is set, not asked again. */
  if (rest.length === 0 && (place.remembered || place.decided)) return { lat: only.lat, lng: only.lng, label: only.label };
  pending.push({ role, name, candidates: place.candidates });
  return null;
}

/**
 * @param current  a mostani állapot; `activityType: null` — még nincs mozgásforma
 *                 (a rögzítés képernyőn a választás előtt, vagy a Home-ról)
 */
export function applyInterpretation(
  result: RouteInterpretResult,
  current: { activityType: ActivityType | null; settings: PlannerSettings },
): AssistantApplication {
  const { interpretation: it, places } = result;
  const applied: string[] = [];
  const notes: string[] = [];
  const pending: PendingPlace[] = [];

  /*
    CSIP CSAK VALÓDI VÁLTOZÁSRA (#79): ha a kért érték már be van állítva, a
    csip azt sugallná, hogy a felhasználó kérte — a modell pedig hajlamos a
    tervező alapértékét visszamondani („Közepes kerülő”, „Gyors”).
  */
  const wanted = it.activity ? ACTIVITY[it.activity] : null;
  const activityType = wanted && wanted !== current.activityType ? wanted : null;
  const effectiveType = activityType ?? current.activityType;
  if (activityType) applied.push(ACTIVITY_LABEL[activityType]);

  const settings: PlannerSettings = { ...current.settings };
  if (it.mode && it.mode !== current.settings.mode) {
    settings.mode = it.mode;
    applied.push(MODE_LABEL[it.mode]);
  }
  if (it.detour && it.detour !== current.settings.detour) {
    settings.detour = it.detour;
    applied.push(DETOUR_LABEL[it.detour]);
  }
  if (it.preference && it.preference !== current.settings.preference) {
    settings.preference = it.preference;
    applied.push(PREFERENCE_LABEL[it.preference]);
  }
  if (it.terrain && it.terrain !== current.settings.terrain) {
    if (ROUTE_ELEVATION_ENABLED) {
      settings.terrain = it.terrain;
      applied.push(TERRAIN_LABEL[it.terrain]);
    } else {
      notes.push(getT().routeAssistant.terrainUnavailable(TERRAIN_LABEL[it.terrain].toLowerCase()));
    }
  }
  if (it.preferCycleways !== null && it.preferCycleways !== current.settings.preferCycleways && effectiveType === 'ride') {
    settings.preferCycleways = it.preferCycleways;
    if (it.preferCycleways) applied.push(getT().routeAssistant.cycleways);
  }

  let from: AssistantApplication['from'] = null;
  if (places.start?.kind === 'current_location') from = 'current';
  else if (places.start) {
    from = resolvePlace('from', places.start, pending, notes);
  }

  /* A hely csipje csak a kész (jóváhagyott) pontra jár — a többit a választó mutatja. */
  const to = places.end ? resolvePlace('to', places.end, pending, notes) : null;
  if (to) applied.push(getT().routeAssistant.destination(to.label ?? places.end?.name ?? ''));
  if (from && from !== 'current') applied.push(getT().routeAssistant.start(from.label ?? places.start?.name ?? ''));

  const stops = places.stops.length
    ? places.stops.map((stop, index) => resolvePlace(index, stop, pending, notes))
    : null;
  stops?.forEach((stop) => {
    if (stop) applied.push(getT().routeAssistant.stop(stop.label ?? ''));
  });

  /*
    ⚠️ A TÁV NEM VÁLT KERÜLŐ-MÉRETRE: a kerülő a szerezhető terület, ez
    játékszabály. Kiírjuk, a kerülőt a felhasználó választja.
  */
  if (it.distanceKm !== null) {
    notes.push(getT().routeAssistant.distance(formatKm(it.distanceKm)));
  }
  if (it.unsupported.length) {
    notes.push(getT().routeAssistant.unsupported(it.unsupported.join(', ')));
  }

  const clear: AssistantApplication['clear'] = [];
  if (places.start?.kind === 'place' && from === null) clear.push('from');
  if (places.end && to === null) clear.push('to');

  return { activityType, settings, from, to, clear, stops, pending, applied, notes };
}

/** A tervező azon része, amit az asszisztens előtölthet. */
interface PlannerFields {
  from: PlannerPoint | null;
  to: PlannerPoint | null;
  stops: (PlannerPoint | null)[];
  settings: PlannerSettings;
}

/**
 * Az alkalmazás a tervező állapotára — tiszta függvény a hookhoz.
 *
 * A `from === 'current'` itt NEM dől el (helymeghatározás kell hozzá, aszinkron):
 * a rajt addig a régi marad, a hívó kéri le a pozíciót.
 */
export function withAssistant<T extends PlannerFields>(prev: T, app: AssistantApplication): T {
  return {
    ...prev,
    settings: app.settings,
    from: app.from && app.from !== 'current' ? app.from : app.clear.includes('from') ? null : prev.from,
    to: app.to ?? (app.clear.includes('to') ? null : prev.to),
    stops: app.stops ?? prev.stops,
  };
}

/* ── Beszélgetés (#79) ──────────────────────────────────────────────── */

/**
 * A tervező piszkozata, amikor a tervező NINCS a képernyőn (Home, vagy a chat
 * a rögzítés képernyőn kívül). A rögzítés képernyőn a tervező maga az
 * igazság forrása (`PlannerBridge`); a piszkozat oda kerül át, amikor a
 * felhasználó a tervezőbe lép.
 */
export interface AssistantDraft extends Omit<PlannerFields, 'from'> {
  activityType: ActivityType | null;
  /** `'current'`: a jelenlegi helyzet — a tervező kéri le, amikor átkerül. */
  from: PlannerPoint | 'current' | null;
}

export function emptyDraft(settings: PlannerSettings): AssistantDraft {
  return { activityType: null, settings, from: null, to: null, stops: [] };
}

/** Az asszisztens változása a piszkozatra — a tervező `withAssistant`-jának párja. */
export function applyToDraft(draft: AssistantDraft, app: AssistantApplication): AssistantDraft {
  const next = withAssistant({ ...draft, from: draft.from === 'current' ? null : draft.from }, app);
  return {
    ...next,
    activityType: app.activityType ?? draft.activityType,
    from: app.from === 'current'
      ? 'current'
      : app.from ?? (app.clear.includes('from') ? null : draft.from),
  };
}

/** A piszkozat egészben, a tervezőre — amikor a felhasználó átlép a tervezőbe. */
export function draftAsApplication(draft: AssistantDraft): AssistantApplication {
  return {
    activityType: draft.activityType,
    settings: draft.settings,
    from: draft.from,
    to: draft.to,
    clear: [],
    stops: draft.stops.length ? draft.stops : null,
    pending: [],
    applied: [],
    notes: [],
  };
}

const CONTEXT_ACTIVITY: Record<ActivityType, 'run' | 'walk' | 'bike'> = { run: 'run', walk: 'walk', ride: 'bike' };
/** A címke nélküli pont (térképi kijelölés) — a modell így tudja, hogy van, csak nincs neve. */
const unnamedPoint = () => getT().routeAssistant.unnamed;
/** A tervező így címkézi a helymeghatározással kitöltött rajtot (`useRoutePlanner`). */
export const CURRENT_POSITION_LABEL = 'Jelenlegi pozícióm';

function pointLabel(point: PlannerPoint | null): string | null {
  return point ? point.label ?? unnamedPoint() : null;
}

/**
 * A tervező állapota a szervernek: ebből tudja a modell, mi van már beállítva,
 * és ezért hordozhat a válasz csak VÁLTOZÁST („inkább a Keletihez”).
 */
export function planContextOf(fields: {
  activityType: ActivityType | null;
  settings: PlannerSettings;
  from: PlannerPoint | 'current' | null;
  to: PlannerPoint | null;
  stops: (PlannerPoint | null)[];
}): AssistantPlanContext {
  const { settings } = fields;
  const from = fields.from === 'current' || (fields.from?.label === CURRENT_POSITION_LABEL || fields.from?.label === 'My current location')
    ? 'current'
    : pointLabel(fields.from);
  return {
    activity: fields.activityType ? CONTEXT_ACTIVITY[fields.activityType] : null,
    mode: settings.mode,
    from,
    to: pointLabel(fields.to),
    stops: fields.stops.filter((stop): stop is PlannerPoint => stop !== null).map((stop) => pointLabel(stop) ?? unnamedPoint()),
    detour: settings.detour,
    preference: settings.preference,
    terrain: ROUTE_ELEVATION_ENABLED ? settings.terrain : null,
    preferCycleways: fields.activityType === 'ride' ? settings.preferCycleways : null,
  };
}

/* ── Továbbkérdezés a kész cél után (#79) ──────────────────────────── */

export type FollowUpField = Exclude<AssistantFollowUpField, 'activity'>;

export interface FollowUp {
  field: FollowUpField;
  question: string;
  options: { value: string; label: string }[];
}

/**
 * A cél jóváhagyása után Geri sorban rákérdez arra, ami a beszélgetésben még
 * nem hangzott el (Geri, #79: „itt folytatódnia kéne a beszélgetésnek”). A
 * kérdések rögzítettek, nem a modell írja őket: nem fogy a keretből, és nem
 * kérdezhet olyat, amit a tervező nem tud. Minden kérdésnél „Mindegy”: a
 * mostani beállítás marad.
 *
 * Mikor értelmes: a kerülő csak körnél (a tervezőlap is csak ott mutatja), a
 * terep csak bekapcsolt domborzatnál, a kerékpárút csak bringánál.
 */
export function nextFollowUp(
  decided: ReadonlySet<string>,
  current: { activityType: ActivityType | null; mode: PlannerSettings['mode'] },
): FollowUp | null {
  const all: (FollowUp & { when: boolean })[] = [
    {
      field: 'mode',
      when: true,
      question: getT().routeAssistant.modeQuestion,
      options: [
        { value: 'loop', label: MODE_LABEL.loop },
        { value: 'direct', label: MODE_LABEL.direct },
      ],
    },
    {
      field: 'detour',
      when: current.mode === 'loop',
      question: getT().routeAssistant.detourQuestion,
      options: [
        { value: 'small', label: DETOUR_LABEL.small },
        { value: 'medium', label: DETOUR_LABEL.medium },
        { value: 'large', label: DETOUR_LABEL.large },
      ],
    },
    {
      /* #86: walking and running — the planner graph has no parks, so ask. */
      field: 'viaGreen',
      when: current.activityType === 'walk' || current.activityType === 'run',
      question: getT().routeAssistant.greenQuestion,
      options: [
        { value: 'park', label: getT().routeAssistant.greenPark },
        { value: 'shortest', label: getT().routeAssistant.greenShortest },
      ],
    },
    {
      field: 'preference',
      when: true,
      question: getT().routeAssistant.roadQuestion,
      options: [
        { value: 'fast', label: PREFERENCE_LABEL.fast },
        { value: 'protected', label: PREFERENCE_LABEL.protected },
        { value: 'quiet', label: PREFERENCE_LABEL.quiet },
      ],
    },
    {
      field: 'terrain',
      when: ROUTE_ELEVATION_ENABLED,
      question: getT().routeAssistant.terrainQuestion,
      options: [
        { value: 'flat', label: TERRAIN_LABEL.flat },
        { value: 'balanced', label: TERRAIN_LABEL.balanced },
        { value: 'hilly', label: TERRAIN_LABEL.hilly },
      ],
    },
    {
      field: 'preferCycleways',
      when: current.activityType === 'ride',
      question: getT().routeAssistant.cyclewayQuestion,
      options: [
        { value: 'true', label: getT().routeAssistant.yes },
        { value: 'false', label: getT().routeAssistant.no },
      ],
    },
  ];
  const next = all.find((item) => item.when && !decided.has(item.field));
  if (!next) return null;
  const { when: _when, ...followUp } = next;
  return followUp;
}

/** A gyorsválasz a beállításokra — a csip szövegével együtt. */
export function applyFollowUp(
  settings: PlannerSettings,
  field: FollowUpField,
  value: string,
): { settings: PlannerSettings; chip: string } {
  switch (field) {
    case 'mode': {
      const mode = value === 'direct' ? 'direct' : 'loop';
      return { settings: { ...settings, mode }, chip: MODE_LABEL[mode] };
    }
    case 'detour': {
      const detour = value === 'small' || value === 'large' ? value : 'medium';
      return { settings: { ...settings, detour }, chip: DETOUR_LABEL[detour] };
    }
    case 'preference': {
      const preference = value === 'protected' || value === 'quiet' ? value : 'fast';
      return { settings: { ...settings, preference }, chip: PREFERENCE_LABEL[preference] };
    }
    case 'terrain': {
      const terrain = value === 'flat' || value === 'hilly' ? value : 'balanced';
      return { settings: { ...settings, terrain }, chip: TERRAIN_LABEL[terrain] };
    }
    case 'viaGreen': {
      /* The park itself is looked up by the store (`answerFollowUp`) — here only the quiet streets. */
      if (value !== 'park') return { settings, chip: getT().routeAssistant.greenShortest };
      return { settings: { ...settings, preference: 'quiet' }, chip: getT().routeAssistant.greenPark };
    }
    case 'preferCycleways': {
      const on = value === 'true';
      return { settings: { ...settings, preferCycleways: on }, chip: on ? getT().routeAssistant.cycleways : getT().routeAssistant.noCycleways };
    }
  }
}

/** Csak a beállítások cseréje — a pontok maradnak (a tervező `withAssistant`-ja ezt így kezeli). */
export function settingsApplication(settings: PlannerSettings): AssistantApplication {
  return { activityType: null, settings, from: null, to: null, clear: [], stops: null, pending: [], applied: [], notes: [] };
}

/**
 * Ennyi tétlenség után a gyors mezőből új beszélgetés indul: a „futnék egyet”
 * holnap reggel ne a tegnapi kérdés folytatása legyen. A chatben a felhasználó
 * maga dönt („Új beszélgetés”).
 */
export const ASSISTANT_CONVERSATION_IDLE_MS = 6 * 60 * 60 * 1000;

export function conversationExpired(lastAt: number, now: number): boolean {
  return lastAt > 0 && now - lastAt > ASSISTANT_CONVERSATION_IDLE_MS;
}

/** Mit mutasson a felület egy sikertelen kérés után. */
export type AssistantFailure = { kind: 'consent' } | { kind: 'message'; text: string };

/**
 * A hiba besorolása. A hozzájárulás hiánya NEM hiba a felhasználó szemében,
 * hanem a hozzájárulás-kártya; minden más a szerver magyar üzenete.
 */
export function classifyAssistantError(error: unknown): AssistantFailure {
  if (error instanceof ApiError) {
    if (error.code === 'assistant_consent_required') return { kind: 'consent' };
    if (error.message) return { kind: 'message', text: error.message };
  }
  return {
    kind: 'message',
    text: getT().routeAssistant.failed,
  };
}

/** „Ma még N kérés” — csak ha kevés maradt. */
export function quotaHint(quotaLeft: number): string | null {
  return quotaLeft < ASSISTANT_QUOTA_WARN_BELOW ? getT().routeAssistant.quota(quotaLeft) : null;
}
